/**
 * Times for a human in one timezone (PLAN §13.1).
 *
 * The server stores and sends UTC. The owner thinks in Africa/Kampala. Every
 * time shown in the interface goes through here so that conversion happens in
 * exactly one place, and so every time carries the relative hint the plan asks
 * for — "today 18:30 · in 3 h" tells you more than either half alone.
 */
import { formatInTimeZone } from 'date-fns-tz';

export const DEFAULT_TIMEZONE = 'Africa/Kampala';

function toDate(value: Date | string): Date {
  return value instanceof Date ? value : new Date(value);
}

/**
 * An unparseable timestamp must not take a screen down.
 *
 * `formatInTimeZone` throws RangeError "Invalid time value" on an Invalid
 * Date, and a field-name drift between the API and the web client is enough
 * to produce one — the post history crashed exactly this way when the API
 * sent `at` and this client read `createdAt`. A dash in one row is a far
 * better failure than an error boundary over the whole page.
 */
function isUsable(value: Date | string): boolean {
  return !Number.isNaN(toDate(value).getTime());
}

/** "18:30" */
export function formatTime(value: Date | string, timezone: string): string {
  return formatInTimeZone(toDate(value), timezone, 'HH:mm');
}

/** "3 Nov" — the year only appears when it is not this one. */
export function formatDate(value: Date | string, timezone: string, now: Date = new Date()): string {
  const date = toDate(value);
  const sameYear =
    formatInTimeZone(date, timezone, 'yyyy') === formatInTimeZone(now, timezone, 'yyyy');
  return formatInTimeZone(date, timezone, sameYear ? 'd MMM' : 'd MMM yyyy');
}

/** "today 18:30", "tomorrow 09:05", "3 Nov 18:30" */
export function formatDayTime(
  value: Date | string,
  timezone: string,
  now: Date = new Date(),
): string {
  const date = toDate(value);
  const day = dayLabel(date, timezone, now);
  return `${day} ${formatTime(date, timezone)}`;
}

function dayLabel(date: Date, timezone: string, now: Date): string {
  const key = (value: Date) => formatInTimeZone(value, timezone, 'yyyy-MM-dd');
  const target = key(date);

  if (target === key(now)) return 'today';
  if (target === key(new Date(now.getTime() + 86_400_000))) return 'tomorrow';
  if (target === key(new Date(now.getTime() - 86_400_000))) return 'yesterday';

  return formatDate(date, timezone, now);
}

/**
 * "in 3 h", "4 min ago", "now".
 *
 * Deliberately coarse. The owner wants to know whether something is imminent,
 * not that it is 2 hours 47 minutes away.
 */
export function formatRelative(value: Date | string, now: Date = new Date()): string {
  const ms = toDate(value).getTime() - now.getTime();
  const future = ms >= 0;
  const abs = Math.abs(ms);

  const minutes = Math.round(abs / 60_000);
  if (minutes < 1) return 'now';

  const render = (amount: number, unit: string): string =>
    future ? `in ${String(amount)} ${unit}` : `${String(amount)} ${unit} ago`;

  if (minutes < 60) return render(minutes, 'min');

  const hours = Math.round(minutes / 60);
  if (hours < 24) return render(hours, 'h');

  const days = Math.round(hours / 24);
  if (days < 7) return render(days, days === 1 ? 'day' : 'days');

  const weeks = Math.round(days / 7);
  return render(weeks, weeks === 1 ? 'week' : 'weeks');
}

/** "today 18:30 · in 3 h" — the plan's exact pairing. */
export function formatWhen(value: Date | string, timezone: string, now: Date = new Date()): string {
  if (!isUsable(value)) return '—';
  return `${formatDayTime(value, timezone, now)} · ${formatRelative(value, now)}`;
}

/** "4:12" or "1:04:12" for a duration in milliseconds. */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const seconds = total % 60;
  const minutes = Math.floor(total / 60) % 60;
  const hours = Math.floor(total / 3600);
  const pad = (value: number) => String(value).padStart(2, '0');

  return hours > 0
    ? `${String(hours)}:${pad(minutes)}:${pad(seconds)}`
    : `${String(minutes)}:${pad(seconds)}`;
}

/** "38.2 MB". Decimal units, because that is what phones and platforms quote. */
export function formatBytes(bytes: number): string {
  if (bytes < 1000) return `${String(bytes)} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1000;
  let unit = 0;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit += 1;
  }
  return `${value.toFixed(value >= 100 ? 0 : 1)} ${units[unit] ?? 'TB'}`;
}

/**
 * The value a `datetime-local` input needs, in the owner's timezone rather
 * than the browser's — the two are often different when travelling, and the
 * schedule must mean what the owner sees.
 */
export function toLocalInputValue(value: Date | string, timezone: string): string {
  return formatInTimeZone(toDate(value), timezone, "yyyy-MM-dd'T'HH:mm");
}

/**
 * The inverse: read a `datetime-local` value as a wall-clock time in the
 * owner's timezone and return the matching instant.
 *
 * Done by probing rather than by table lookup, so daylight-saving and offset
 * changes are handled by the same library that formats them.
 */
export function fromLocalInputValue(local: string, timezone: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(local)) return null;

  // Start by assuming UTC, then correct by the offset that guess lands in.
  const guess = new Date(`${local}:00Z`);
  if (Number.isNaN(guess.getTime())) return null;

  const offsetMinutes = (at: Date): number => {
    const stamp = formatInTimeZone(at, timezone, 'xxx'); // "+03:00"
    const sign = stamp.startsWith('-') ? -1 : 1;
    const [hours = '0', minutes = '0'] = stamp.slice(1).split(':');
    return sign * (Number(hours) * 60 + Number(minutes));
  };

  let result = new Date(guess.getTime() - offsetMinutes(guess) * 60_000);
  // One correction pass catches the case where the first guess fell on the
  // other side of a daylight-saving boundary.
  result = new Date(guess.getTime() - offsetMinutes(result) * 60_000);
  return result;
}
